<script setup>
import { computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue';
import ThemesEditorSidebar from './ThemesEditorSidebar';
import ThemesEditorTabs from './ThemesEditorTabs';
import ThemesEditorCodemirror from './ThemesEditorCodemirror';

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const route = proxy.$route;
const router = proxy.$router;

// --- Computed ---

const activeEditorKey = computed(() => store.state.themes.activeEditorKey);
const editors = computed(() => store.state.themes.editors);
const codeMirror = computed(() => store.state.themes.codeMirror);

// --- Methods ---

const findKeyboardShortcut = (e) => {
    if (
        e.metaKey // CMD key
        && e.shiftKey
        && e.keyCode === 70 // F key
    ) {
        e.preventDefault();

        if (codeMirror.value) {
            let str = codeMirror.value.getSelection();
            if (str) {
                // If the search string doesn't match the previous string then this must be reset.
                if (store.state.themes.searchStr !== str) {
                    store.state.themes.findModalItemIndex = 0;
                }
                store.state.themes.searchStr = str;
            }
        }

        store.dispatch('modals/show', {
            componentName: 'ThemesFindModal'
        });
    }
};

// --- Lifecycle (Created logic) ---

window.addEventListener('keydown', findKeyboardShortcut);

// If returning to Themes and active tabs are open route to active tab
if (!route.query.section && editors.value.length) {
    const editor = editors.value[activeEditorKey.value];
    router.push({ query: { section: editor.sectionName, id: editor.id } });
}

store.dispatch('themes/init');
store.dispatch('themes/setSectionName', route.query.section || 'page_templates');
store.commit('navigationComponent', 'themes_editor');

// --- Lifecycle (Destroyed logic) ---

onBeforeUnmount(() => {
    window.removeEventListener('keydown', findKeyboardShortcut);
});

// --- Watchers ---

watch(() => proxy.$route, (to, from) => {
    if (to.query.section && to.query.id) {
        if (
            !from // Reloading the UI
            || to.query.section !== from.query.section
            || to.query.id !== from.query.id
        ) {
            store.dispatch('themes/createEditor', {
                sectionName: to.query.section,
                id: to.query.id,
            });
        }
    }
}, { immediate: true });
</script>

<template>
    <div class="d-flex h-100">
        <aside class="w-25 overflow-hidden">
            <ThemesEditorSidebar/>
        </aside>

        <main class="flex-fill w-100 overflow-hidden">
            <div class="d-flex flex-column h-100">
                <div class="themes-editor-toolbar overflow-x-scroll" data-themes-editor-toolbar>
                    <ThemesEditorTabs/>
                </div>

                <div v-for="(editor, index) in editors" v-if="index === activeEditorKey" :key="editor.key"
                     class="themes-editor--editor-container">
                    <ThemesEditorCodemirror :editor="editor" :focused="editor.focused" :latest-code="editor.latestCode"
                                            class="d-flex flex-column h-100"/>
                </div>
            </div>
        </main>
    </div>
</template>

<style scoped>
.themes-editor--editor-container {
    height: calc(100% - 38px);
}

.themes-editor-toolbar::-webkit-scrollbar {
    display: none;
}
</style>